import { useMemo, useState, useCallback, useEffect } from "react";
import { useLocation, useNavigate, useNavigationType } from "react-router-dom";
import * as yup from "yup";
import Icon from "@mdi/react";
import { v4 as uuidv4 } from "uuid";
import { Box } from "@mui/system";
import {
  Button,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemSecondaryAction,
  ListItemText,
  Switch as MuiSwitch,
} from "@mui/material";
import {
  mdiAccountOutline,
  mdiAlertOutline,
  mdiBellOutline,
  mdiDeleteOutline,
  mdiEmailOutline,
  mdiExportVariant,
  mdiFormatColorFill,
  mdiHelpCircleOutline,
  mdiKeyOutline,
  mdiShieldLinkVariantOutline,
  mdiTagOutline,
  mdiThemeLightDark,
} from "@mdi/js";
import { Capacitor } from "@capacitor/core";

import AppBar from "components/AppBar";
import AvatarPicker from "components/AvatarPicker";
import PageLayout from "components/PageLayout";
import ShareButton from "components/ShareButton";
import DeleteAccountDialog from "containers/Settings/DeleteAccountDialog";
import EditAppColorDialog from "containers/Settings/EditAppColorDialog";
import EditThemeDialog from "containers/Settings/EditThemeDialog";
import EditUserDataDialog from "containers/Settings/EditUserDataDialog";
import { SetInput, SetModal } from "containers/Settings/EditUserDataDialog/EditUserDataDialog";
import useNotifier from "hooks/useNotifier";
import useBoundStore from "store";
import capitalizeFirstLetter from "utils/capitalizeFirstLetter";
import generateTag from "utils/generateTag";
import getNameInitials from "utils/getNameInitials";
import resizeAvatarImage from "utils/resizeAvatarImage";
import AuthApi from "api/auth";
import ProfileApi from "api/profile";
import StorageApi, { StorageBuckets } from "api/storage";
import LocalStorage from "services/LocalStorage";
import googleCalendarIcon from "assets/icons/google-calendar.png";
import { getHost, getNotification, isIOS, isInStandaloneMode, supportNotification } from "utils/device";
import {
  getOptedIn,
  oneSignalHasNotificationsDenied,
  oneSignalOptIn,
  oneSignalOptOut,
  oneSignalShowNativePrompt,
} from "services/OneSignal";
import DataIconPlaceholder from "components/DataIconPlaceholder";
import logSentryError from "utils/logSentryError";
import styles from "./settings.module.scss";

type DialogType = "user" | "theme" | "delete" | "color";

interface ListItemType {
  primary: string;
  secondary: string;
  icon: string;
  modalBody: string;
  modalInputValue: string;
  inputType?: string;
  inputValidation: yup.ObjectSchema<{ inputValue: string }>;
  hide?: boolean;
  onSubmit: (newName: string) => Promise<void>;
}

const nameValidationSchema = yup.object().shape({
  inputValue: yup
    .string()
    .required("Name is required")
    .min(5, "Name must be longer than 5 characters")
    .matches(/^[a-zA-Z-â-ț-ș\s]*$/, "Name can only contain letters and spaces"),
});

const emailValidationSchema = yup.object().shape({
  inputValue: yup.string().trim().required("Email address is required").email("Email address is not valid"),
});

const passwordValidationSchema = yup.object().shape({
  inputValue: yup.string().required("Password is required").min(8, "Password must be longer than 8 characters"),
});

interface SettingsProps {
  onBack?: () => void;
  onViewHelp: () => void;
}

function Settings(props: SettingsProps) {
  const { onBack, onViewHelp } = props;

  const [modal, setModal] = useState<SetModal>({
    title: "",
    body: "",
    onSubmit: async () => undefined,
  });
  const [input, setInput] = useState<SetInput>({
    label: "",
    icon: "",
    value: "",
    type: "text",
    validationSchema: null,
  });
  const [dialogType, setDialogType] = useState<DialogType | null>(null);
  const [profileImage, setProfileImage] = useState<Blob | null>(null);
  const user = useBoundStore((state) => state.user);
  const generalSettings = useBoundStore((state) => state.generalSettings);
  const setSmallAvatars = useBoundStore((state) => state.miscellaneous.set);
  const notification = useBoundStore((state) => state.notification);
  const navigate = useNavigate();
  const location = useLocation();
  const navigationType = useNavigationType();
  const localStorage = LocalStorage.getInstance();
  const { showNotification } = useNotifier();
  const authApi = useMemo(() => new AuthApi(showNotification), [showNotification]);
  const profileApi = useMemo(() => new ProfileApi(showNotification), [showNotification]);
  const storageApi = useMemo(() => new StorageApi(showNotification), [showNotification]);

  /**
   * Get user profile
   */
  const getUserProfile = useCallback(async (): Promise<void> => {
    if (user.auth) {
      const userProfile = await profileApi.getUserProfile(user.auth.id);
      user.set({ profile: userProfile });
    }
  }, [profileApi, user.auth]);

  /**
   * Set user profile
   */
  const getUserAvatar = useCallback(async () => {
    if (user.profile?.avatar) {
      const { file } = await storageApi.getFile(user.profile?.avatar, StorageBuckets.PROFILES_AVATARS);
      setProfileImage(file);

      if (user.profile?.avatar && file) {
        setSmallAvatars({ smallAvatars: { [user.profile.avatar]: file } });
      }
    }
  }, [user.profile, storageApi, setSmallAvatars]);

  /**
   * Save image to storage bucket
   *
   * @param image
   */
  async function handleSaveProfileAvatar(image: Blob): Promise<void> {
    setProfileImage(null);
    const { bigAvatarImg, smallAvatarImg } = await resizeAvatarImage(image);
    const imageName = `${uuidv4()}.jpg`;

    // Save both big and small profile image to storage
    const { error: bigAvatarError } = await storageApi.saveImage(
      bigAvatarImg,
      imageName,
      StorageBuckets.PROFILES_AVATARS
    );
    const { error: smallAvatarError } = await storageApi.saveImage(
      smallAvatarImg,
      `s-${imageName}`,
      StorageBuckets.PROFILES_AVATARS
    );

    if (!bigAvatarError && !smallAvatarError) {
      let deleteError;
      // Remove old profile images (both big and small one)
      if (user.profile?.avatar) {
        ({ error: deleteError } = await storageApi.deleteFiles(StorageBuckets.PROFILES_AVATARS, [
          user.profile.avatar,
          `s-${user.profile.avatar}`,
        ]));
      }

      // Update user profile with the new profile image
      if (!deleteError && user.auth) {
        await profileApi.updateProfileImage(user.auth.id, imageName);
        await getUserProfile();

        if (user.profile?.avatar) {
          setSmallAvatars({ smallAvatars: { [user.profile!.avatar]: image } });
        }

        setProfileImage(image);
      }
    }
  }

  /**
   * Handle delete profile image
   */
  async function handleDeleteProfileImage(): Promise<void> {
    if (user.profile?.avatar) {
      const { error } = await storageApi.deleteFiles(StorageBuckets.PROFILES_AVATARS, [
        user.profile.avatar,
        `s-${user.profile.avatar}`,
      ]);

      if (!error && user.auth) {
        await profileApi.updateProfileImage(user.auth.id, "");
        user.set({ profile: { ...user.profile, avatar: null } });

        setProfileImage(null);
      }
    }
  }

  /**
   * Handle switch change
   *
   * @param event
   * @param checked
   */
  function handleSwitchChange(event: React.ChangeEvent<HTMLInputElement>, checked: boolean): void {
    const switchName = event.target.name as "externalLinkNavigation" | "notifications" | "googleCalendar";

    if (switchName === "externalLinkNavigation") {
      localStorage.setConfirmExternalLinkNavigation(checked);
      generalSettings.set({ externalLinkNavigation: checked });
    } else if (switchName === "googleCalendar") {
      localStorage.setGoogleCalendarEvent(checked);
      generalSettings.set({ googleCalendar: checked });
    } else if (switchName === "notifications") {
      if (checked) {
        oneSignalOptIn();
      } else {
        oneSignalOptOut();
      }
      notification.set({ optedIn: checked });
    }
  }

  /**
   * Handle open dialog
   *
   * @param type
   */
  function handleOpenDialog(type: DialogType): void {
    setDialogType(type);
    navigate(location.pathname);
  }

  /**
   * Handle close dialog
   */
  function handleCloseDialog(): void {
    setDialogType(null);
    navigate(-1);
  }

  useEffect(() => {
    if (notification.optedIn === null) {
      getOptedIn().then((optedIn) => {
        notification.set({ optedIn });
      });
    }
  }, [notification.optedIn]);

  /**
   * Get avatar on component mount
   */
  useEffect(() => {
    getUserAvatar();
  }, [getUserAvatar]);

  /**
   * On pressing back button close opened dialogs
   */
  useEffect(() => {
    if (navigationType === "POP") {
      setDialogType(null);
    }
  }, [navigationType]);

  function renderAppBar(): JSX.Element {
    return <AppBar title='Settings' onBack={onBack} />;
  }

  if (!user.profile || !user.auth) {
    logSentryError(new Error("Settings: User profile or auth is missing."));
    return (
      <PageLayout appBar={renderAppBar()}>
        <DataIconPlaceholder icon={mdiAlertOutline} text='Unable to fetch settings' />
      </PageLayout>
    );
  }

  // TODO: render the list without things related to user.profile and user.auth
  const listData: ListItemType[] = [
    {
      primary: "Name",
      secondary: user.profile.name ?? "",
      icon: mdiAccountOutline,
      modalBody: "Change your account name",
      modalInputValue: user.profile.name ?? "",
      inputValidation: nameValidationSchema,
      onSubmit: async (newName: string): Promise<void> => {
        try {
          // Check if name is different from the old one
          if (newName === user.profile!.name) {
            return showNotification({
              message: "Name not changed as it remains the same.",
              severity: "warning",
            });
          }
          const lowerCaseName = newName.toLowerCase();

          // Get users with same name
          const { tags, error: getUsersProfilesError } = await profileApi.getTagsByName(lowerCaseName);

          if (getUsersProfilesError) {
            throw new Error(getUsersProfilesError.message);
          }

          // Create the profile of the user
          const tag = generateTag(lowerCaseName, tags);

          if (!tag) {
            throw Error("Unable to generate tag.");
          }

          // Update user name
          const { profile, error: updateNameError } = await profileApi.updateUserName(user.auth!.id, {
            name: newName,
            lowerCaseName,
            tag,
          });
          if (updateNameError) {
            throw new Error(updateNameError.message);
          }
          user.set({ profile });

          showNotification({
            message: "Successfully changed user name.",
            severity: "success",
          });
        } catch (error: any) {
          showNotification({ message: error.message, severity: "error" });
        }

        return undefined;
      },
    },
    {
      primary: "Email",
      secondary: user.auth.email || "",
      icon: mdiEmailOutline,
      modalBody:
        "Change your account email. After this action you'll be logged out and you must verify your email address to be able to log in again.",
      modalInputValue: user.auth.email || "",
      inputValidation: emailValidationSchema,
      hide: user.auth?.provider === "google",
      onSubmit: async (newEmail: string): Promise<void> => {
        // Check if email is different from the old one
        if (newEmail === user.auth?.email) {
          showNotification({
            message: "Email not changed as it remains the same.",
            severity: "warning",
          });
          return undefined;
        }

        // Change email
        await authApi.updateEmail(newEmail);
        return undefined;
      },
    },
    {
      primary: "Password",
      secondary: "Change your account password",
      icon: mdiKeyOutline,
      modalBody: "Change your account password",
      modalInputValue: "",
      inputType: "password",
      inputValidation: passwordValidationSchema,
      hide: user.auth?.provider === "google",
      onSubmit: async (newPassword: string) => {
        // Change password
        await authApi.updatePassword(newPassword);
      },
    },
  ];

  function handleListItemClick(listItem: ListItemType): void {
    setModal({
      title: `Change ${listItem.primary.toLowerCase()}`,
      body: listItem.modalBody,
      onSubmit: listItem.onSubmit,
    });
    setInput({
      label: listItem.primary,
      icon: listItem.icon,
      value: listItem.modalInputValue,
      type: listItem.inputType || "text",
      validationSchema: listItem.inputValidation,
    });

    handleOpenDialog("user");
  }

  /**
   * Generate the link used to be shared with other to be able to add them as
   * friends
   *
   * @returns
   */
  function generateLinkFromTag(): string | undefined {
    if (user?.profile?.tag) {
      const tagParts: string[] = user.profile.tag.split("#");
      return `${getHost()}/friends/add-friend?name=${tagParts[0]}&hash=${tagParts[1]}`;
    }

    throw new Error("generateLinkFromTag: Unable to generate link from tag.");
  }

  return (
    <PageLayout appBar={renderAppBar()}>
      <Box m={2} display='flex' justifyContent='center'>
        <AvatarPicker
          id='settings-avatar'
          img={profileImage ? URL.createObjectURL(profileImage) : undefined}
          placeholder={getNameInitials(user.profile.name)}
          onSelectImage={handleSaveProfileAvatar}
          onDeleteImage={handleDeleteProfileImage}
        />
      </Box>

      <List dense>
        {/* TAG */}
        <ListItem>
          <ListItemIcon>
            <Icon path={mdiTagOutline} size={1} />
          </ListItemIcon>
          <ListItemText primary='GroupUp tag' secondary={user.profile.tag || "Loading..."} />
          <ListItemSecondaryAction>
            <ShareButton data={{ text: generateLinkFromTag() }} loading={!user.profile.tag} />
          </ListItemSecondaryAction>
        </ListItem>

        {listData.map((listItem) =>
          listItem.hide ? null : (
            <div key={listItem.primary}>
              <ListItemButton onClick={() => handleListItemClick(listItem)}>
                <ListItemIcon>
                  <Icon path={listItem.icon} size={1} />
                </ListItemIcon>
                <ListItemText primary={listItem.primary} secondary={listItem.secondary} />
              </ListItemButton>
            </div>
          )
        )}

        {/* CHANGE THEME */}
        <ListItemButton onClick={() => handleOpenDialog("theme")}>
          <ListItemIcon>
            <Icon path={mdiThemeLightDark} size={1} />
          </ListItemIcon>
          <ListItemText primary='Theme' secondary={capitalizeFirstLetter(generalSettings.theme)} />
        </ListItemButton>

        {/* CHANGE APP COLOR */}
        <ListItemButton onClick={() => handleOpenDialog("color")}>
          <ListItemIcon>
            <Icon path={mdiFormatColorFill} size={1} className={styles.appColorIcon} />
          </ListItemIcon>
          <ListItemText primary='App color' secondary={capitalizeFirstLetter(generalSettings.primaryColor)} />
        </ListItemButton>

        {/* CONFIRM LINK NAVIGATION */}
        <ListItem>
          <ListItemIcon>
            <Icon path={mdiShieldLinkVariantOutline} size={1} />
          </ListItemIcon>
          <ListItemText
            primary='Confirm link navigation'
            secondary='Show confirmation dialog when pressing on external links'
          />
          <MuiSwitch
            edge='end'
            name='externalLinkNavigation'
            onChange={handleSwitchChange}
            checked={generalSettings.externalLinkNavigation}
            inputProps={{
              "aria-label": "Toggle confirm link navigation",
            }}
          />
        </ListItem>

        {/* Notification */}
        <NotificationListItem
          optedIn={notification.optedIn ?? false}
          onAllow={() => {
            oneSignalShowNativePrompt().then((granted) => notification.set({ optedIn: granted }));
          }}
          onChange={handleSwitchChange}
        />

        {/* Google Calendar  */}
        <ListItem>
          <ListItemIcon>
            <img src={googleCalendarIcon} height={24} width={24} alt='google calendar icon' />
          </ListItemIcon>
          <ListItemText
            primary='Google Calendar event'
            secondary='Add events to Google Calendar when you participate.'
          />
          <MuiSwitch
            edge='end'
            name='googleCalendar'
            onChange={handleSwitchChange}
            checked={generalSettings.googleCalendar}
            inputProps={{
              "aria-label": "Toggle use Google Calendar",
            }}
          />
        </ListItem>

        {/* HELP */}
        <ListItemButton onClick={onViewHelp}>
          <ListItemIcon>
            <Icon path={mdiHelpCircleOutline} size={1} />
          </ListItemIcon>
          <ListItemText primary='Help' secondary='About us, App info, Privacy Policy, Terms and Conditions' />
        </ListItemButton>

        {/* DELETE ACCOUNT */}
        <ListItemButton onClick={() => handleOpenDialog("delete")}>
          <ListItemIcon>
            <Icon path={mdiDeleteOutline} size={1} />
          </ListItemIcon>
          <ListItemText
            primary='Delete account'
            secondary="Warning! This action can't be undone and all your data will be lost"
          />
        </ListItemButton>
      </List>

      <EditUserDataDialog input={input} modal={modal} onClose={handleCloseDialog} open={dialogType === "user"} />
      <EditThemeDialog onClose={handleCloseDialog} open={dialogType === "theme"} />
      <EditAppColorDialog onClose={handleCloseDialog} open={dialogType === "color"} />
      <DeleteAccountDialog onClose={handleCloseDialog} open={dialogType === "delete"} />
    </PageLayout>
  );
}

export default Settings;

interface NotificationListItemProps {
  optedIn: boolean;
  onAllow: () => void;
  onChange: (event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => void;
}

function NotificationListItem(props: NotificationListItemProps) {
  const { optedIn, onAllow, onChange } = props;

  let secondaryText: React.ReactNode | Element = "Receive notifications for new events and friend requests";
  let action: JSX.Element | null = (
    <MuiSwitch
      edge='end'
      name='notifications'
      onChange={onChange}
      checked={optedIn}
      inputProps={{
        "aria-label": "Toggle notifications",
      }}
    />
  );

  if (oneSignalHasNotificationsDenied() && !optedIn) {
    if (Capacitor.isNativePlatform()) {
      secondaryText = "To receive notifications, you must allow them from application settings.";
      action = (
        <Button variant='outlined' size='small' onClick={onAllow}>
          Allow
        </Button>
      );
    } else {
      secondaryText = "To receive notifications, you must allow them from the browser settings.";
      action = null;
    }
  }

  if (getNotification()?.permission === "default") {
    secondaryText = "Disabled";
    action = (
      <Button variant='outlined' size='small' onClick={onAllow}>
        Allow
      </Button>
    );
  }

  if (!supportNotification()) {
    secondaryText = "Your device does not support notifications.";
    action = null;
  }

  if (isIOS() && supportNotification() && !isInStandaloneMode()) {
    secondaryText = (
      <span>
        To receive notifications you must add GroupUp to your home screen. To do this, press on
        <Icon className={styles.exportVariantIcon} path={mdiExportVariant} size={0.75} />
        and then &#39;Add to Home Screen&#39;.
      </span>
    );
    action = null;
  }

  return (
    <ListItem>
      <ListItemIcon>
        <Icon path={mdiBellOutline} size={1} />
      </ListItemIcon>
      <ListItemText primary='Notifications' secondary={secondaryText} />
      {action}
    </ListItem>
  );
}
